import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface UserGroup {
  id: string;
  raw: string;
  name: string;
  description: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly keycloak = inject(Keycloak);
  private readonly http = inject(HttpClient);

  private readonly userSubject = new ReplaySubject<KeycloakProfile>(1);
  user$ = this.userSubject.asObservable();
  private currentUserProfile: KeycloakProfile | null = null;

  private readonly groupsSubject = new BehaviorSubject<UserGroup[]>([]);
  groups$ = this.groupsSubject.asObservable();

  private readonly indexDescriptionsMap = new Map<string, string>();

  constructor() {
    this.loadUserProfile();
  }

  private decodeToken(token?: string): any {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const base64Url = parts[1];
      const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + (c.codePointAt(0) ?? 0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error decoding token', e);
      return null;
    }
  }

  public getParsedToken(): any {
    return (
      (this.keycloak.tokenParsed as any) ||
      (this.keycloak.idTokenParsed as any) ||
      this.decodeToken(this.keycloak.token) ||
      this.decodeToken(this.keycloak.idToken) ||
      null
    );
  }

  public getUserEmail(): string | null {
    const token = this.getParsedToken();
    if (token?.email) {
      return token.email;
    }
    if (this.currentUserProfile?.email) {
      return this.currentUserProfile.email;
    }
    return null;
  }

  public getUserGroups(): UserGroup[] {
    const token = this.getParsedToken();
    if (!token) return [];

    let rawGroups: string[] = [];
    if (Array.isArray(token.groups)) {
      rawGroups = token.groups;
    } else if (typeof token.groups === 'string') {
      rawGroups = [token.groups];
    }

    let rawDescriptions: string[] = [];
    if (Array.isArray(token.group_descriptions)) {
      rawDescriptions = token.group_descriptions;
    } else if (Array.isArray(token.group_description)) {
      rawDescriptions = token.group_description;
    } else if (typeof token.group_descriptions === 'string') {
      rawDescriptions = [token.group_descriptions];
    } else if (typeof token.group_description === 'string') {
      rawDescriptions = [token.group_description];
    }

    return rawGroups.map((g, index) => {
      const raw = g;
      const name = g.startsWith('/') ? g.substring(1) : g;
      const cleanName = name.toLowerCase();

      // 1. Fonte primaria autoritativa: Descrizione registrata sull'indice da Ingestion API (Soluzione C)
      const backendDesc = this.indexDescriptionsMap.get(cleanName);

      // 2. Fallback semantico: Corrispondenza nome-gruppo dentro la descrizione di Keycloak
      const matchedDesc = rawDescriptions.find((desc) =>
        desc.toLowerCase().includes(cleanName)
      );

      // 3. Fallback finale: Posizionale oppure nome del gruppo
      const description =
        backendDesc ||
        matchedDesc ||
        rawDescriptions[index] ||
        name;

      return {
        id: name,
        raw,
        name,
        description,
      };
    });
  }

  private loadUserProfile() {
    const initialGroups = this.getUserGroups();
    if (initialGroups.length > 0) {
      this.groupsSubject.next(initialGroups);
    }

    this.keycloak
      .loadUserProfile()
      .then((profile) => {
        this.currentUserProfile = profile;
        this.userSubject.next(profile);
        this.fetchOfficialIndexDescriptions();
      })
      .catch(() => {
        this.fetchOfficialIndexDescriptions();
      });
  }

  public fetchOfficialIndexDescriptions() {
    this.groupsSubject.next(this.getUserGroups());

    const indexEndpoint = `${environment.baseUrl}/index`;
    this.http.get<any[]>(indexEndpoint).subscribe({
      next: (indexes) => {
        if (Array.isArray(indexes)) {
          for (const idx of indexes) {
            if (idx?.name) {
              const desc = idx.description || idx.name;
              this.indexDescriptionsMap.set(idx.name.toLowerCase(), desc);
            }
          }
          this.groupsSubject.next(this.getUserGroups());
        }
      },
      error: (err) => {
        console.warn('Impossibile recuperare metadati indici da Ingestion API, uso fallback:', err);
      },
    });
  }

  login(): void {
    this.keycloak.login();
  }
  logout(): void {
    this.keycloak.logout();
  }

  getKeycloakId(): string {
    return this.keycloak.idTokenParsed?.sub ?? '';
  }
}
